import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuthentication } from '../../Login/hooks/useAuthentication';
import { ApplicationEntryLayout } from '../components/ApplicationEntryLayout/ApplicationEntryLayout';
import { ValidationGroup } from 'devextreme-react/validation-group';
import { useValidateAndSaveApplication } from '../hooks/useValidateAndSaveApplication.ts';
import { BorrowerForm } from './BorrowerForm.tsx';
import { useSaveApplicationWithoutValidation } from '../hooks/useSaveApplicationWithoutValidation.ts';
import { useRedirectIfComplete } from '../hooks/useRedirectIfApplicationComplete.ts';
import { CurrentApplicationPage, useNavigateApplication } from '../hooks/useNavigateApplication.ts';
import { usePausePointTracker } from '../hooks/usePausePointTracker.tsx';
import { useApplicationQuery } from '../../queries/applicationQueries';
import { useBorrowerPrincipalInit } from './queries/useBorrowerPrincipalInit';
import { useLoanTypeCache } from '../../queries/useLoanTypeCache';
import { useWaitOverlayStore } from '@apply/components';
import { ApplicationCrumbType } from '../components/ApplicationEntryLayout/components/ApplicationEntrySections/types/ApplicationCrumbType.tsx';

// Outer shell: sets up ApplicationEntryGuidProvider and starts the data fetch
export const Borrower: React.FC = () => {
    const { entryGuid } = useParams<{ entryGuid?: string }>();
    const { search } = useLocation();
    const token = new URLSearchParams(search).get('token');

    // Start the fetch unconditionally (caches in React Query)
    const { isPending } = useApplicationQuery(entryGuid, token);

    // Dismiss wait overlay when loading completes
    useEffect(() => {
        if (!isPending) {
            useWaitOverlayStore.getState().setWaiting(false);
        }
    }, [isPending]);

    useRedirectIfComplete();

    const { authentication, requiresESign } = useAuthentication();

    const validationGroupRef = useRef(null);
    const { validateAndSaveApplication } = useValidateAndSaveApplication(validationGroupRef);
    const { saveApplicationNoValidation } = useSaveApplicationWithoutValidation();

    const [generatedEntryGuid, set_generatedEntryGuid] = useState<string | undefined>(undefined);

    const loanType = useLoanTypeCache();

    const [triggerNavigateNext, set_triggerNavigateNext] = useState<boolean>(false);
    const { navigateApplicationForward } = useNavigateApplication({
        page: CurrentApplicationPage.Borrower,
        entryGuid: entryGuid || generatedEntryGuid,
    });

    const navigate = useNavigate();
    const [disableWizardButtons, set_disableWizardButtons] = useState(true);
    const { updatePausePoint, clearPausePoint } = usePausePointTracker();

    // Pre-fill principal fields from auth for new applications
    useBorrowerPrincipalInit(authentication);

    useEffect(() => {
        if (!triggerNavigateNext || (!generatedEntryGuid && !entryGuid)) return;
        navigateApplicationForward();
    }, [triggerNavigateNext, entryGuid, generatedEntryGuid]);

    const nextButtonClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        clearPausePoint();

        const applicationResponse = await validateAndSaveApplication();
        if (!applicationResponse) return;

        set_generatedEntryGuid(applicationResponse.entryGuid);
        set_triggerNavigateNext(true);
    };

    const saveAndExitClick = async (e: React.MouseEvent<Element, MouseEvent>) => {
        if (e) e.preventDefault();

        if (loanType) {
            updatePausePoint();

            const applicationResponse = await saveApplicationNoValidation();
            if (!applicationResponse) return;
        }

        navigate('/home');
    };

    if (isPending) {
        return null;
    }

    if (requiresESign) {
        return <Navigate to={`/signagreement`} />;
    }

    const nextStepButton = {
        id: 'saveAndContinueApplicationButton',
        dataTestid: 'saveAndContinueApplicationButton',
        disabled: !loanType || disableWizardButtons,
        children: 'Save and Continue',
        handleClick: nextButtonClick,
    };

    const saveAndExitButton = {
        id: 'saveAndExitApplicationButton',
        dataTestid: 'saveAndExitApplicationButton',
        children: !loanType ? 'Exit' : 'Save and Exit',
        handleClick: saveAndExitClick,
    };

    return (
        <ValidationGroup ref={validationGroupRef}>
            <ApplicationEntryLayout
                currentCrumb={ApplicationCrumbType.BasicInfo}
                saveAndExitButtonProps={saveAndExitButton}
                nextStepButtonProps={nextStepButton}
                previousStepButtonVisible={false}
            >
                <BorrowerForm
                    entryGuid={entryGuid}
                    isReview={false}
                    set_disableWizardButtons={set_disableWizardButtons}
                />
            </ApplicationEntryLayout>
        </ValidationGroup>
    );
};
